import { useState, useEffect, ReactNode, RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

interface AnchoredHintBubbleProps {
  show: boolean;
  onDismiss: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  dismissAriaLabel?: string;
}

export function AnchoredHintBubble({
  show,
  onDismiss,
  anchorRef,
  children,
  dismissAriaLabel = "Dismiss hint",
}: AnchoredHintBubbleProps) {
  const [dismissed, setDismissed] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    right: number;
    tailLeft: number;
    visible: boolean;
  } | null>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!show || dismissed) return;

    function measure() {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      const offscreen = rect.bottom < 0 || rect.top > window.innerHeight;
      setPosition({
        top: rect.bottom + 8,
        left: rect.left,
        right: window.innerWidth - rect.right,
        tailLeft: rect.left + rect.width / 2,
        visible: !offscreen,
      });
    }

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true, capture: true });
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, { capture: true } as EventListenerOptions);
    };
  }, [show, dismissed, anchorRef]);

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss();
  };

  const visible = show && !dismissed && position !== null && position.visible;

  return (
    <AnimatePresence>
      {visible && position && (
        <motion.div
          key="anchored-hint-bubble"
          initial={prefersReducedMotion ? false : { opacity: 0, y: -6, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.97 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            right: position.right,
            zIndex: 75,
          }}
        >
          {/* Upward-pointing tail centred on the anchor */}
          <div
            className="absolute -top-2 w-0 h-0"
            style={{
              left: position.tailLeft - position.left - 8,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderBottom: "8px solid #3b82f6",
            }}
          />
          <div
            className="relative rounded-lg border bg-zinc-950 p-3 pr-9 shadow-xl shadow-black/50 font-sans max-w-xs"
            style={{ borderColor: "#3b82f6" }}
          >
            <button
              onClick={handleDismiss}
              aria-label={dismissAriaLabel}
              className="absolute top-2 right-2 p-1 rounded text-zinc-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-sm leading-snug text-zinc-200">{children}</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface MobileWelcomeBubbleProps {
  show: boolean;
  onDismiss: () => void;
  feedbackRef: RefObject<HTMLElement | null>;
}

export function MobileWelcomeBubble({ show, onDismiss, feedbackRef }: MobileWelcomeBubbleProps) {
  return (
    <AnchoredHintBubble
      show={show}
      onDismiss={onDismiss}
      anchorRef={feedbackRef}
      dismissAriaLabel="Dismiss welcome message"
    >
      <p>Welcome to The Carry Network. Please leave any feedback in here. Thanks for joining!</p>
    </AnchoredHintBubble>
  );
}
